// Claude model pricing. Values are USD per 1M tokens, base (non-cached) rates.
//
// These are hardcoded against Anthropic's public pricing page as of 2026-04.
// Prompt caching (server-side) can reduce real cost significantly — cache
// reads bill at ~10% of the base input rate — so the numbers computed here
// are best read as an *upper bound* on what the session actually cost.
//
// Anthropic does NOT expose a public "credit balance remaining" endpoint for
// regular API keys. The only ways to see real spend are the Admin API (needs
// an admin-level key tied to your org) or the Console web UI. So we show the
// user our own cumulative count and are upfront that it's an estimate.

interface ModelPricing {
  /** USD per 1,000,000 input tokens (base rate, no cache discount). */
  inputPerMillion: number;
  /** USD per 1,000,000 output tokens. */
  outputPerMillion: number;
}

const PRICING: Record<string, ModelPricing> = {
  // Sonnet family
  "claude-sonnet-4-6": { inputPerMillion: 3, outputPerMillion: 15 },
  "claude-sonnet-4-5": { inputPerMillion: 3, outputPerMillion: 15 },
  sonnet: { inputPerMillion: 3, outputPerMillion: 15 },
  // Opus family
  "claude-opus-4-6": { inputPerMillion: 15, outputPerMillion: 75 },
  "claude-opus-4-5": { inputPerMillion: 15, outputPerMillion: 75 },
  opus: { inputPerMillion: 15, outputPerMillion: 75 },
  // Haiku family
  "claude-haiku-4-5": { inputPerMillion: 1, outputPerMillion: 5 },
  haiku: { inputPerMillion: 1, outputPerMillion: 5 },
};

/** Fallback when the stored model string doesn't match any known key. */
const DEFAULT_PRICING: ModelPricing = PRICING.sonnet!;

function lookupPricing(model: string | null | undefined): ModelPricing {
  if (!model) return DEFAULT_PRICING;
  const normalized = model.toLowerCase().trim();
  if (PRICING[normalized]) return PRICING[normalized]!;
  // Loose match: "claude-sonnet-4-6[1m]" etc.
  for (const key of Object.keys(PRICING)) {
    if (normalized.includes(key)) return PRICING[key]!;
  }
  return DEFAULT_PRICING;
}

/**
 * Upper-bound cost estimate for a single prompt in USD. Uses base input rate —
 * does NOT subtract any prompt cache discount, so the real bill is usually
 * lower than what this returns.
 */
export function calculateCost(
  model: string | null | undefined,
  inputTokens: number | null | undefined,
  outputTokens: number | null | undefined,
): number {
  const pricing = lookupPricing(model);
  const inCost = ((inputTokens ?? 0) / 1_000_000) * pricing.inputPerMillion;
  const outCost = ((outputTokens ?? 0) / 1_000_000) * pricing.outputPerMillion;
  return inCost + outCost;
}

/** Format a token count as "12.3k" / "1.2M" for compact display. */
export function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

/** Format a USD amount — sub-cent shows as "<$0.01", else two decimals. */
export function formatCost(usd: number): string {
  if (usd <= 0) return "$0.00";
  if (usd < 0.01) return "<$0.01";
  if (usd < 100) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(0)}`;
}
